import { DocumentRecordSentimentFlag, StrategyDetailsPageGetDocumentRecordQuery } from '@/__generated__/graphql';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { RejectedIcon } from '@/assets/icons/info-icons/rejected';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { AiAssistantIcon } from '@/assets/static/icons/ai-assistant';
import { RevertIcon } from '@/assets/static/icons/revert-icon';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import DatePicker from '@/components/basicComponents/datePicker';
import { FormFooter } from '@/components/basicComponents/formFooter';
import Input from '@/components/basicComponents/input';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select from '@/components/basicComponents/select';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitleWrap } from '@/components/header/pageTitle';
import { TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { IFieldEditor, IFieldEditorPromptGroup } from '@/shared/types/fieldEditor';
import { FieldConfigurationModalPage } from '@/shared/ui/fieldConfigurationModalPage';
import { FieldPromptEditorModalPage } from '@/shared/ui/fieldPromptEditorModalPage';
import { extractAndFormatDate } from '@/shared/utils/fieldEditor';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { isValid as isValidDate } from 'date-fns';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { STRATEGY_DETAILS_PAGE_UPDATE_DOCUMENT_FIELD_ANSWER_MUTATION, STRATEGY_DETAILS_PAGE_UPDATE_DOCUMENT_RECORD_MUTATION } from '../queries';

export interface IBasicInfoSectionData {
  name: StrategyDetailsPageGetDocumentRecordQuery['getDocumentRecord']['fields'][number];
  publishDate: StrategyDetailsPageGetDocumentRecordQuery['getDocumentRecord']['fields'][number];
  sentimentFlag: DocumentRecordSentimentFlag;
  sentimentFlagLabel: string;
}

interface IForm {
  name: {
    isAiAnswer: boolean;
    fieldId: string;
    value: string;
  };
  publishDate: {
    isAiAnswer: boolean;
    fieldId: string;
    value: string;
  };
  sentimentFlag: string;
  sentimentFlagLabel: string;
}

const defaultFormValues: IForm = {
  name: {
    isAiAnswer: false,
    fieldId: '',
    value: ''
  },
  publishDate: {
    isAiAnswer: false,
    fieldId: '',
    value: null
  },
  sentimentFlag: '',
  sentimentFlagLabel: ''
};

type ModalPageType = 'default' | 'fieldConfiguration' | 'promptEditor';

const flagThisUpdateOptions = [
  { label: 'None', value: '' },
  { label: 'Positive', value: 'Positive' },
  { label: 'Neutral', value: 'Neutral' },
  { label: 'Negative', value: 'Negative' }
];

interface EditBasicInfoProps {
  basicInfoSectionData: IBasicInfoSectionData | null;
  aiButtonConfig: {
    isLoading?: boolean;
    secondaryIcon?: React.ReactNode;
    onClick: () => void;
  };
  refetch: () => Promise<ApolloQueryResult<StrategyDetailsPageGetDocumentRecordQuery>>;
  handleClose: () => void;
}

export const EditBasicInfo = ({ basicInfoSectionData, aiButtonConfig, refetch, handleClose }: EditBasicInfoProps) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, reset, setValue, watch, handleSubmit } = useForm<IForm>({ defaultValues: defaultFormValues });

  const newNameFieldValue = useWatch({ name: 'name.value', control });
  const newPublishDateFieldValue = useWatch({ name: 'publishDate.value', control });

  const [allFields, setAllFields] = useState<IFieldEditor[] | null>(null);
  const [currentField, setCurrentField] = useState<IFieldEditor | null>(null);
  const [modalPageType, setModalPageType] = useState<ModalPageType>('default');
  const [updateDocumentRecordLoading, setUpdateDocumentRecordLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showAiAnswerControl, setShowAiAnswerControl] = useState(false);
  const [showRevertToPriorDraftButton, setShowRevertToPriorDraftButton] = useState(false);
  const [isEditMode, setIsEditMode] = useState(true);

  useEffect(() => {
    if (!basicInfoSectionData) return;
    const { name, publishDate, sentimentFlag, sentimentFlagLabel } = basicInfoSectionData;

    if ((name && name?.value === null && name.AI?.answers.length) || (publishDate && publishDate?.value === null && publishDate.AI?.answers.length)) {
      setIsEditMode(false);
    }

    if (name?.AI?.answers?.length || publishDate?.AI?.answers?.length) {
      setShowAiAnswerControl(true);
    }

    if (name && name?.value === null && name.AI?.answers.length) {
      setValue('name.isAiAnswer', true);
      setValue('name.value', name.AI.answers[0]);
      setValue('name.fieldId', name?.fieldId || '');
    } else {
      setValue('name.isAiAnswer', false);
      setValue('name.value', name?.value || '');
      setValue('name.fieldId', name?.fieldId || '');
    }

    if (publishDate && publishDate?.value === null && publishDate.AI?.answers.length) {
      setValue('publishDate.isAiAnswer', true);
      setValue('publishDate.value', extractAndFormatDate(publishDate.AI?.answers[0]));
      setValue('publishDate.fieldId', publishDate?.fieldId || '');
    } else {
      setValue('publishDate.isAiAnswer', false);
      setValue('publishDate.value', publishDate?.value || '');
      setValue('publishDate.fieldId', publishDate?.fieldId || '');
    }

    setValue('sentimentFlag', sentimentFlag || '');
    setValue('sentimentFlagLabel', sentimentFlagLabel || '');
  }, [basicInfoSectionData]);

  useEffect(() => {
    if (!basicInfoSectionData || modalPageType !== 'default') return;
    const { name, publishDate } = basicInfoSectionData;
    const allEditFields: IFieldEditor[] = [name, publishDate].map((field) => ({
      id: field.id,
      fieldId: field.fieldId,
      fieldAnswerId: field.documentField.id,
      recordId: field.recordId,
      name: field.documentField.name,
      documentFieldType: field.documentField.type,
      value: field.value,
      commentary: field.commentary,
      attachments:
        field?.attachments?.map((attachment) => ({
          id: attachment.id,
          url: attachment.secure_blob_storage_asset.url,
          assetKey: attachment.secure_blob_storage_asset.assetKey
        })) || [],
      AI: field.AI,
      promptGroup: field.promptGroup
        ? {
            id: field.promptGroup?.id,
            prompts: field.promptGroup?.prompts,
            summaryPrompt: field.promptGroup?.summaryPrompt,
            contextPrompt: field.promptGroup?.contextPrompt,
            outputStructure: field.promptGroup?.outputStructure,
            useCustomContextPrompt: field.promptGroup?.useCustomContextPrompt,
            useCustomSummaryPrompt: field.promptGroup?.useCustomSummaryPrompt
          }
        : field.promptGroup
    }));
    setAllFields(allEditFields);
    setCurrentField(allEditFields?.[0] || null);
  }, [basicInfoSectionData]);

  useEffect(() => {
    const { name, publishDate } = basicInfoSectionData || {};

    const prevNameFieldValue = name?.value;
    const prevPublishDateFieldValue = publishDate?.value;

    setShowRevertToPriorDraftButton(
      (!!prevNameFieldValue && prevNameFieldValue !== newNameFieldValue) ||
        (!!prevPublishDateFieldValue && prevPublishDateFieldValue !== newPublishDateFieldValue)
    );
  }, [newNameFieldValue, newPublishDateFieldValue]);

  const [updateDocumentRecord] = useMutation(STRATEGY_DETAILS_PAGE_UPDATE_DOCUMENT_RECORD_MUTATION);
  const [updateDocumentFieldAnswer] = useMutation(STRATEGY_DETAILS_PAGE_UPDATE_DOCUMENT_FIELD_ANSWER_MUTATION);

  const onSubmit = async (data: IForm) => {
    try {
      setUpdateDocumentRecordLoading(true);
      setErrorMessage(null);

      const { name, publishDate, sentimentFlag, sentimentFlagLabel, ...rest } = data;
      const { recordId } = basicInfoSectionData?.name || basicInfoSectionData?.publishDate || {};

      await updateDocumentFieldAnswer({
        variables: {
          data: {
            recordId,
            fieldId: name.fieldId,
            value: !name.isAiAnswer ? name.value : null
          }
        }
      });

      await updateDocumentFieldAnswer({
        variables: {
          data: {
            recordId,
            fieldId: publishDate.fieldId,
            value: !publishDate.isAiAnswer ? publishDate.value : null
          }
        }
      });

      await updateDocumentRecord({
        variables: {
          data: {
            id: recordId,
            sentimentFlag: (sentimentFlag as DocumentRecordSentimentFlag) || null,
            sentimentFlagLabel: sentimentFlag ? sentimentFlagLabel : null
          }
        }
      });

      await refetch();
      handleClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setUpdateDocumentRecordLoading(false);
    }
  };

  const clearSuggestion = async () => {
    reset((prevData) => ({
      name: {
        ...prevData.name,
        isAiAnswer: false,
        value: ''
      },
      publishDate: {
        ...prevData.publishDate,
        isAiAnswer: false,
        value: null
      },
      sentimentFlag: '',
      sentimentFlagLabel: ''
    }));
    setIsEditMode(true);
  };

  const applyAISuggestion = (mode: 'startOver' | 'adoptAndEdit') => {
    const { name, publishDate } = basicInfoSectionData || {};
    const isStartOver = mode === 'startOver';
    const currentPublishDateIsAiAnswer = watch('publishDate.isAiAnswer');

    const nameAiAnswer = name?.AI?.answers?.[0] || '';
    const publishDateAiAnswer = extractAndFormatDate(publishDate?.AI?.answers?.[0] || '');

    const getPublishDateValue = (): string | null => {
      if (isStartOver) {
        return publishDateAiAnswer;
      }
      if (currentPublishDateIsAiAnswer) {
        return publishDateAiAnswer;
      }
      return publishDate?.value || publishDateAiAnswer;
    };

    reset((prevData) => ({
      ...prevData,
      name: {
        ...prevData.name,
        isAiAnswer: isStartOver,
        value: nameAiAnswer
      },
      publishDate: {
        ...prevData.publishDate,
        isAiAnswer: isStartOver,
        value: getPublishDateValue()
      }
    }));

    setIsEditMode(mode === 'adoptAndEdit');
  };

  const revertToPriorDraft = () => {
    const { name, publishDate } = basicInfoSectionData || {};

    reset((prevData) => ({
      ...prevData,
      name: {
        ...prevData.name,
        isAiAnswer: false,
        value: name?.value || ''
      },
      publishDate: {
        ...prevData.publishDate,
        isAiAnswer: false,
        value: publishDate?.value || ''
      }
    }));

    setIsEditMode(true);
  };

  const savePromptGroup = (promptGroup: IFieldEditorPromptGroup) => {
    const updatedCurrentField = {
      ...currentField,
      promptGroup: promptGroup
    };
    setCurrentField(updatedCurrentField);
  };

  const handleModalPageType = (type: ModalPageType) => {
    setModalPageType(type);
  };

  const handleCurrentFieldChange = (id: string) => {
    const selectedField = allFields?.find((field) => field.id === id);
    if (selectedField) {
      setCurrentField(selectedField);
    }
  };

  return (
    <>
      {modalPageType === 'fieldConfiguration' && (
        <FieldConfigurationModalPage
          allFields={allFields}
          currentField={currentField}
          handleCurrentFieldChange={handleCurrentFieldChange}
          openPromptEditorModalPage={() => setModalPageType('promptEditor')}
          handleClose={() => setModalPageType('default')}
        />
      )}
      {modalPageType === 'promptEditor' && (
        <FieldPromptEditorModalPage
          handleClose={() => setModalPageType('fieldConfiguration')}
          currentRow={currentField}
          currentRowName={currentField?.name ?? ''}
          refetch={refetch}
          savePromptGroup={savePromptGroup}
        />
      )}
      {modalPageType === 'default' && (
        <>
          {updateDocumentRecordLoading && (
            <ModalWrappedContent
              isOpen={updateDocumentRecordLoading}
              title="Saving"
              showCancelButton={false}
              showCloseIcon={false}
              onClose={() => {}}
            >
              <ModalLoader />
            </ModalWrappedContent>
          )}
          <MainWrap>
            <Header aiButtonConfig={aiButtonConfig} modalControl={<GoBackButton handleClose={handleClose} backToTitle="Edit Update" />} />

            <CustomPageTitleWrap withBorder>
              Basic Info
              {user && user.roles.includes('Super Admin') && (
                <Button size="md" styleType="outline" onClick={() => handleModalPageType('fieldConfiguration')}>
                  Configure Field
                </Button>
              )}
            </CustomPageTitleWrap>
            {errorMessage && (
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            )}
            {showAiAnswerControl && (
              <AiAnswerControl>
                {isEditMode ? (
                  <>
                    <AiAnswerControlButtons>
                      <Button size="sm" onClick={() => applyAISuggestion('startOver')} styleType="outline">
                        <AiAssistantIcon width={16} height={16} fill={themeVar('action.default')} />
                        Start Over With AI Suggestion
                      </Button>
                      {showRevertToPriorDraftButton && (
                        <Button size="sm" onClick={revertToPriorDraft} styleType="outline">
                          <RevertIcon fill={themeVar('action.default')} />
                          Revert to Prior Draft
                        </Button>
                      )}
                    </AiAnswerControlButtons>
                    <AiAnswerControlButtons>
                      <Button size="sm" onClick={clearSuggestion} styleType="outline">
                        <RejectedIcon width={16} height={16} fill={themeVar('action.default')} />
                        Clear
                      </Button>
                    </AiAnswerControlButtons>
                  </>
                ) : (
                  <>
                    <AiSuggestionHeader>
                      <MagicWandIconWrapper>
                        <AiAssistantIcon width={28} height={28} fill={themeVar('font.weak')} />
                      </MagicWandIconWrapper>
                      Suggested:
                      {showRevertToPriorDraftButton && (
                        <Button size="sm" onClick={revertToPriorDraft} styleType="outline">
                          <RevertIcon fill={themeVar('action.default')} />
                          Revert to Prior Draft
                        </Button>
                      )}
                    </AiSuggestionHeader>
                    <AiAnswerControlButtons>
                      <Button size="sm" onClick={() => applyAISuggestion('adoptAndEdit')}>
                        <ApprovedIcon width={16} height={16} fill={themeVar('context.light')} />
                        Adopt and Edit Suggested
                      </Button>
                      <Button size="sm" onClick={clearSuggestion} styleType="outline">
                        <RejectedIcon width={16} height={16} fill={themeVar('action.default')} />
                        Clear
                      </Button>
                    </AiAnswerControlButtons>
                  </>
                )}
              </AiAnswerControl>
            )}
            <PaddingWrap>
              <Form id="managerUpdatesBasicInfoForm" onSubmit={handleSubmit(onSubmit)}>
                <FormFieldsWrapper>
                  <TwoColumnWrapper isMobile={isMobile}>
                    <Controller
                      name="name"
                      control={control}
                      rules={{
                        validate: {
                          requiredName: (name) => {
                            if (!name.value) {
                              return 'Name is required';
                            }
                            return true;
                          }
                        }
                      }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Input
                          label="Name"
                          value={value.value || ''}
                          onChange={(e) => {
                            onChange({ ...value, value: e.target.value });
                          }}
                          styleType={value.isAiAnswer ? 'magicWand' : 'default'}
                          errorText={error?.message}
                          isRequiredField
                          size="md"
                        />
                      )}
                    />
                    <Controller
                      name="publishDate"
                      control={control}
                      rules={{
                        validate: {
                          requiredPublishDate: (publishDate) => {
                            if (!publishDate.value) {
                              return 'Publish Date is required';
                            }
                            return true;
                          },
                          isValidDate: (publishDate) => {
                            if (isValidDate(new Date(publishDate.value))) {
                              return true;
                            }
                            return 'Invalid date';
                          }
                        }
                      }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <DatePicker
                          label="Publish Date"
                          value={value.value || null}
                          onChange={(date) => {
                            onChange({ ...value, value: date ? new Date(date) : null });
                          }}
                          styleType={value.isAiAnswer ? 'magicWand' : 'default'}
                          maxDate={dayjs()}
                          errorText={error?.message}
                          isRequiredField
                          size="md"
                        />
                      )}
                    />
                  </TwoColumnWrapper>
                  <TwoColumnWrapper isMobile={isMobile}>
                    <Controller
                      name="sentimentFlag"
                      control={control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Select
                          label="Flag This Update"
                          value={value || ''}
                          options={flagThisUpdateOptions}
                          setValue={onChange}
                          errorText={error?.message}
                          defaultSelectLabel="None"
                          size="md"
                        />
                      )}
                    />
                    {!!watch('sentimentFlag') && (
                      <Controller
                        name="sentimentFlagLabel"
                        control={control}
                        rules={{
                          required: {
                            value: true,
                            message: 'Flag Description is required'
                          },
                          validate: (value) => value?.length <= 15 || 'Please limit this value to 15 characters or less'
                        }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <Input
                            label="Flag Description"
                            value={value || ''}
                            onChange={(e) => {
                              onChange(e.target.value);
                            }}
                            errorText={error?.message}
                            isRequiredField
                            size="md"
                          />
                        )}
                      />
                    )}
                  </TwoColumnWrapper>
                </FormFieldsWrapper>
                <FormFooter onCancel={handleClose} disableSaveButton={false} />
              </Form>
            </PaddingWrap>
          </MainWrap>
        </>
      )}
    </>
  );
};

const CustomPageTitleWrap = styled(PageTitleWrap)`
  justify-content: space-between;
`;

const Form = styled.form`
  padding: 50px 0;
  width: 100%;
  background: ${themeVar('layer.base')};
`;

const FormFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  padding: 40px;
  border-radius: 10px;
  border: 1px solid ${themeVar('border.base')};
  background: ${themeVar('layer.1')};
`;

const AiAnswerControl = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 10px 60px;
  border-bottom: 1px solid ${themeVar('border.base')};
  background: ${themeVar('layer.1')};
`;

const AiAnswerControlButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const AiSuggestionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 700;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
`;

const MagicWandIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 10px;
  border-right: 1px solid ${themeVar('border.base')};
`;
